import { transformGitHubPRUrl } from './url-transformers';

const EMPTY_ANCHOR_REGEX = /<a\b[^>]*>(?:&nbsp;|\s|<br\s*\/?>)*<\/a>/gi;
const EMPTY_CONTAINER_REGEX = /<(p|div)\b[^>]*>(?:&nbsp;|\s|<br\s*\/?>)*<\/\1>/gi;
const PROTECTED_MEDIA_BLOCK_REGEX = /<(picture|video|audio)\b[\s\S]*?<\/\1>/gi;
const CURSOR_FIX_CTA_BLOCK_REGEX =
  /<div\b[^>]*>((?:(?!<\/div>)[\s\S])*?<img\b[^>]*\salt=(?:"|')Fix in Cursor(?:"|')[^>]*>(?:(?!<\/div>)[\s\S])*?<img\b[^>]*\salt=(?:"|')Fix in Web(?:"|')[^>]*>(?:(?!<\/div>)[\s\S])*?)<\/div>/gi;
const CURSOR_FIX_CTA_SPAN_REGEX =
  /(<span[^>]*class="[^"]*markdown-linked-image-cta[^"]*"[^>]*>)([\s\S]*?)(<\/span>)/gi;

function removeCursorFixCtaSources(html: string): string {
  return html.replace(CURSOR_FIX_CTA_SPAN_REGEX, (_match, openTag, content, closeTag) => {
    return `${openTag}${content.replace(/<source\b[^>]*>/gi, '')}${closeTag}`;
  });
}

function hasMeaningfulNodeContent(element: Element): boolean {
  if (element.querySelector('img, picture, video, svg, code, pre, table, details, ul, ol, li')) {
    return true;
  }

  const normalizedText = element.textContent?.replace(/\u00A0/g, ' ').trim() ?? '';
  return normalizedText.length > 0;
}

function cleanupMalformedHtmlWithoutDomParser(processedHtml: string): string {
  const protectedBlocks: string[] = [];
  // Unguessable per call, so content that contains a placeholder-like string cannot
  // receive a media block in its place.
  const placeholderPrefix = `__PROTECTED_MEDIA_BLOCK_${globalThis.crypto.randomUUID()}_`;
  let cleanedHtml = processedHtml.replace(PROTECTED_MEDIA_BLOCK_REGEX, (match) => {
    const placeholder = `${placeholderPrefix}${protectedBlocks.length}__`;
    protectedBlocks.push(match);
    return placeholder;
  });

  cleanedHtml = cleanedHtml.replace(/<source\b[^>]*>/gi, '');
  cleanedHtml = cleanedHtml.replace(EMPTY_ANCHOR_REGEX, '');

  let previousHtml: string | null = null;
  while (cleanedHtml !== previousHtml) {
    previousHtml = cleanedHtml;
    cleanedHtml = cleanedHtml.replace(EMPTY_CONTAINER_REGEX, '');
  }

  protectedBlocks.forEach((block, index) => {
    cleanedHtml = cleanedHtml.replace(`${placeholderPrefix}${index}__`, () => block);
  });

  return removeCursorFixCtaSources(cleanedHtml);
}

export function cleanupRenderedHtml(processedHtml: string): string {
  if (typeof DOMParser === 'undefined') {
    return cleanupMalformedHtmlWithoutDomParser(processedHtml);
  }

  const document = new DOMParser().parseFromString(processedHtml, 'text/html');

  document.querySelectorAll('source').forEach((sourceElement) => {
    if (!sourceElement.closest('video, audio, picture')) {
      sourceElement.remove();
    }
  });
  document.querySelectorAll('.markdown-linked-image-cta source').forEach((sourceElement) => {
    sourceElement.remove();
  });

  document.querySelectorAll('a').forEach((anchorElement) => {
    if (!hasMeaningfulNodeContent(anchorElement)) {
      anchorElement.remove();
    }
  });

  let removedNode = true;
  while (removedNode) {
    removedNode = false;
    document.querySelectorAll('p, div').forEach((containerElement) => {
      if (!hasMeaningfulNodeContent(containerElement)) {
        containerElement.remove();
        removedNode = true;
      }
    });
  }

  return document.body.innerHTML;
}

export const processHtml = (rawHtml: string) => {
  // Store all code blocks before processing
  const codeBlocks: string[] = [];

  // Extract all <pre> blocks completely and replace with placeholders
  let processedHtml = rawHtml.replace(/<pre[\s\S]*?<\/pre>/g, (match) => {
    const index = codeBlocks.length;
    const placeholder = `__CODE_BLOCK_PLACEHOLDER_${index}__`;

    // Add styling to the match if needed
    let styledMatch = match;
    if (!match.includes('class=')) {
      styledMatch = match.replace(
        '<pre',
        '<pre class="bg-zinc-800 rounded p-2 overflow-x-auto my-2"',
      );
    }

    codeBlocks.push(styledMatch);
    return placeholder;
  });

  // Process everything except code blocks

  // Transform GitHub PR URLs to cubic.dev URLs in anchor tags
  processedHtml = processedHtml.replace(
    /<a([^>]*?)href="(https:\/\/github\.com\/[^/]+\/[^/]+\/pull\/\d[^"]*)"([^>]*)>/gi,
    (_match, beforeHref, githubUrl, afterHref) => {
      const cubicUrl = transformGitHubPRUrl(githubUrl);
      return `<a${beforeHref}href="${cubicUrl}"${afterHref}>`;
    },
  );

  // Convert markdown-style video links to video elements, handling encoded URLs
  processedHtml = processedHtml.replace(
    /<img[^>]*?src="([^"]*?\.(?:mp4|webm|ogg|mov|%2Emp4|%2Ewebm|%2Eogg|%2Emov))"[^>]*>/gi,
    (match, src) => {
      // Extract data-canonical-src if it exists
      const canonicalSrcMatch = match.match(/data-canonical-src="([^"]*)"/);
      const videoSrc = canonicalSrcMatch ? canonicalSrcMatch[1] : src;
      return `<video controls preload="metadata" class="w-full max-w-3xl rounded-lg my-2" src="${videoSrc}"></video>`;
    },
  );

  // Remove wrapping anchor tags around video elements
  processedHtml = processedHtml.replace(/<a[^>]*>\s*(<video[^>]*>.*?<\/video>)\s*<\/a>/gi, '$1');

  processedHtml = processedHtml.replace(
    CURSOR_FIX_CTA_BLOCK_REGEX,
    '<span class="markdown-linked-image-cta">$1</span>',
  );

  // Process table elements with alignment
  processedHtml = processedHtml
    .replace(/<table>/g, '<table class="w-full border-collapse my-4 border border-zinc-700">')
    .replace(/<thead>/g, '<thead class="bg-zinc-800">')
    .replace(/<tbody>/g, '<tbody class="bg-transparent">')
    .replace(/<tr>/g, '<tr class="border-t border-zinc-800">')
    .replace(
      /<th([^>]*)align="([^"]*)"([^>]*)>/g,
      (_, before, align, after) =>
        `<th${before}${after} class="p-2 text-${align} font-semibold border border-zinc-800">`,
    )
    .replace(
      /<td([^>]*)align="([^"]*)"([^>]*)>/g,
      (_, before, align, after) =>
        `<td${before}${after} class="p-2 text-${align} border border-zinc-800">`,
    )
    .replace(
      /<th(?![^>]*class)[^>]*>/g,
      '<th class="p-2 text-left font-semibold border border-zinc-800">',
    )
    .replace(/<td(?![^>]*class)[^>]*>/g, '<td class="p-2 border border-zinc-800">');

  // Now apply other HTML transformations
  processedHtml = processedHtml
    .replace(/<h1[^>]*>/g, '<h1>')
    .replace(/<h2[^>]*>/g, '<h2>')
    .replace(/<h3[^>]*>/g, '<h3>')
    .replace(/<p\b[^>]*>/g, '<p>')
    .replace(/<strong>/g, '<strong class="font-semibold">')
    .replace(/<ul>/g, '<ul class="list-disc pl-6 my-2">')
    .replace(/<ol>/g, '<ol class="list-decimal pl-6 my-2">')
    .replace(/<blockquote>/g, '<blockquote class="border-l-4 border-zinc-600/60 pl-4 italic my-2">')
    .replace(/<code>/g, '<code class="bg-zinc-800 rounded px-1">')
    .replace(/<em>/g, '<em class="italic">')
    .replace(
      /<details[^>]*class="[^"]*">/g,
      '<details open class="details-reset border rounded-2">',
    )
    .replace(
      /<summary[^>]*class="[^"]*">/g,
      '<summary class="px-0 py-0 flex items-center hover:bg-zinc-800/50 cursor-pointer text-xs leading-tight">',
    )
    .replace(/<span[^>]*aria-label=[^>]*>/g, '<span class="text-xs text-zinc-500 leading-tight">');

  processedHtml = processedHtml.replace(/<img\b([^>]*)>/gi, (_match, attributes: string) => {
    const classMatch = attributes.match(/\bclass="([^"]*)"/i);
    const mergedClassName = classMatch
      ? Array.from(new Set(`${classMatch[1]} rounded-lg`.trim().split(/\s+/))).join(' ')
      : 'rounded-lg';
    let nextAttributes = classMatch
      ? attributes.replace(/\bclass="[^"]*"/i, () => `class="${mergedClassName}"`)
      : `${attributes} class="${mergedClassName}"`;

    if (!/\bloading="/i.test(nextAttributes)) {
      nextAttributes += ' loading="lazy"';
    }

    if (!/\bdecoding="/i.test(nextAttributes)) {
      nextAttributes += ' decoding="async"';
    }

    return `<img${nextAttributes}>`;
  });

  // Now restore code blocks
  for (let i = 0; i < codeBlocks.length; i++) {
    const placeholder = `__CODE_BLOCK_PLACEHOLDER_${i}__`;
    const codeBlock = codeBlocks[i];
    if (codeBlock) {
      processedHtml = processedHtml.replace(new RegExp(placeholder, 'g'), () => codeBlock);
    }
  }

  return processedHtml;
};
